// Show solution / replay (spec §8.5, §9.2): the main line on the board, the
// variations listed below; clicking a move goes there, arrow keys step.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LegalMove, Solution } from "../api/client";
import { Board, type Arrow, type BoardHandle } from "../board/Board";
import { useT } from "../i18n";
import { usePrefs } from "../lib/prefs";
import { lines, moveNumber, walkLine } from "../lib/solution";

type Props = {
  solution: Pick<Solution, "textfen" | "solution" | "positions"> & { user_moves?: Solution["user_moves"] };
  extraArrows?: Arrow[];
  autoplay?: boolean;
};

export function SolutionView({ solution, extraArrows = [], autoplay = true }: Props) {
  const t = useT();
  const prefs = usePrefs();
  const board = useRef<BoardHandle>(null);
  const all = useMemo(() => lines(solution.solution as never), [solution.solution]);
  const positions = solution.positions as unknown as Record<string, LegalMove[]>;
  const [lineIndex, setLineIndex] = useState(0);
  const [ply, setPly] = useState(0);
  const [shown, setShown] = useState(solution.textfen);
  const [userArrow, setUserArrow] = useState<Arrow | null>(null);
  const blackFirst = solution.textfen[0] === "B" ? 1 : 0; // numbering starts "1..."
  const line = all[lineIndex] ?? { moves: [], main: true, diverges: 0 };
  const walked = useMemo(() => walkLine(solution.textfen, line.moves, positions), [solution.textfen, line.moves, positions]);
  const last = ply > 0 ? walked.details[ply - 1] : null;

  const goto = useCallback(
    async (target: number, animateStep = true) => {
      setUserArrow(null);
      target = Math.max(0, Math.min(walked.details.length, target));
      if (animateStep && target === ply + 1 && board.current) {
        setPly(target);
        await board.current.play(walked.details[ply]);
        setShown(walked.fens[target]);
      } else {
        setPly(target);
        setShown(walked.fens[target]);
      }
    },
    [ply, walked],
  );

  // play the main line once, at a calm pace
  useEffect(() => {
    if (!autoplay) return;
    let cancelled = false;
    (async () => {
      for (let k = 0; k < walked.details.length && !cancelled; k++) {
        await new Promise((r) => setTimeout(r, k === 0 ? 400 : 650));
        if (cancelled || !board.current) return;
        setPly(k + 1);
        await board.current.play(walked.details[k]);
        setShown(walked.fens[k + 1]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // only on first show
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT" || (e.target as HTMLElement)?.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight") void goto(ply + 1);
      else if (e.key === "ArrowLeft") void goto(ply - 1, false);
      else if (e.key === "Home") void goto(0, false);
      else if (e.key === "End") void goto(walked.details.length, false);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goto, ply, walked.details.length]);

  function selectLine(i: number, k: number) {
    setLineIndex(i);
    const w = walkLine(solution.textfen, all[i].moves, positions);
    setPly(k);
    setShown(w.fens[k]);
    setUserArrow(null);
  }

  function showUserMove(index: number) {
    const m = solution.user_moves![index];
    const legal = positions[m.position] ?? [];
    const d = legal.find((x) => x.move === m.move);
    setShown(m.position);
    setUserArrow(
      d ? { from: d.from, to: d.to, path: d.path, color: m.in_tree ? "var(--success)" : "var(--error)" } : null,
    );
  }

  const arrows = [...extraArrows, ...(userArrow ? [userArrow] : [])];

  return (
    <div className="stack">
      <Board
        ref={board}
        position={shown}
        lastMove={userArrow ? null : last}
        arrows={arrows}
        coordinates={prefs.coordinates}
        label={t("sol.title")}
      />
      <div className="row" style={{ justifyContent: "center" }}>
        <button className="ghost" onClick={() => goto(0, false)} aria-label="start">⏮</button>
        <button className="ghost" onClick={() => goto(ply - 1, false)} aria-label="back">◀</button>
        <button className="ghost" onClick={() => goto(ply + 1)} aria-label="forward">▶</button>
        <button className="ghost" onClick={() => goto(walked.details.length, false)} aria-label="end">⏭</button>
      </div>
      <div className="card small" style={{ padding: 12 }}>
        {all.map((l, i) => (
          <div key={i} style={{ marginBottom: 6, lineHeight: 1.9 }}>
            <span className="muted" style={{ marginRight: 6 }}>{l.main ? t("sol.main") : "↳"}</span>
            {l.moves.map((mv, k) =>
              k < l.diverges ? null : (
                <span key={k}>
                  <span className="muted">{moveNumber(k + blackFirst, k === l.diverges)} </span>
                  <button
                    className="ghost num"
                    style={{
                      padding: "1px 4px",
                      fontWeight: lineIndex === i && ply === k + 1 ? 700 : 500,
                      background: lineIndex === i && ply === k + 1 ? "var(--accent-soft)" : undefined,
                    }}
                    onClick={() => selectLine(i, k + 1)}
                  >
                    {mv}
                  </button>{" "}
                </span>
              ),
            )}
          </div>
        ))}
        {solution.user_moves && solution.user_moves.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <span className="muted" style={{ marginRight: 6 }}>{t("sol.your_moves")}</span>
            {solution.user_moves.map((m, i) => (
              <button
                key={i}
                className="ghost num"
                style={{ padding: "1px 5px", color: m.in_tree ? "var(--success)" : "var(--error)" }}
                onClick={() => showUserMove(i)}
              >
                {m.move}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

